import { ToastError, ToastSuccess, ToastInfo } from "components/Global/Toasts/Toasty";
import { takeLatest } from "redux-saga/effects";
import { defaultReturn, generateDefaultActions, makePayload, makeWritePayload, requestSaga } from "shared-utility/store/utils/helpers";

const drawerSections = [
  {
    name: "field-management",
    goToButtonText: "Go to Source Files",
    goToButtonValue: "source-file-management",
    deleteText: "Delete all fields",
    deleteHelperText: "Deleting all fields will remove all of their underlying content and data.",
    title: "Fields"
  },
  {
    name: "source-file-management",
    goToButtonText: "Go to Fields",
    goToButtonValue: "field-management",
    deleteText: "Delete all source files",
    deleteHelperText: "Deleting all source files will remove all of their underlying content and data including any fields that were created with them.",
    title: "Source Files"
  },
  {
    name: "line-item-management",
    title: "Insert new line item"
  },
  {
    name: "comparison-field-selection",
    title: "Select a formula field"
  },
  {
    name: "dscr-line-items",
    title: "Debt Service definition"
  },
  {
    name: "debt-yield-line-items",
    title: "Select calculation method"
  }
];

const initialState = {
  currentSectionName: null,
  currentSection: null,
  dealSourceFiles: null,
  isFromLineItemManagement: null,
  isEditingLineItem: false,
  drawerMinWidth: "950px",
  allFilesForDeal: null,
  isDeleteDialogOpen: false,
  fieldIdsToDelete: null
};

// ACTIONS
export const GET_SOURCE_FILES_BY_DEAL = generateDefaultActions("GET_SOURCE_FILES_BY_DEAL");
export const getSourceFilesByDeal = payload => makePayload(GET_SOURCE_FILES_BY_DEAL.value, payload);
function* getSourceFilesByDealSaga({ dealId }) {
  const url = `/source-files/deal/${dealId}`;
  yield requestSaga(GET_SOURCE_FILES_BY_DEAL, url, "GET", null, null, null, true);
}

export const GET_ALL_FILES_FOR_DEAL = generateDefaultActions("GET_ALL_FILES_FOR_DEAL");
export const getAllFilesForDeal = payload => makePayload(GET_ALL_FILES_FOR_DEAL.value, payload);
function* getAllFilesForDealSaga({ dealId }) {
  const url = `/source-files/allFilesForDeal/${dealId}`;
  yield requestSaga(GET_ALL_FILES_FOR_DEAL, url, "GET", null, null, null, true);
}

export const SET_LENDER_REPORTING_DRAWER_SELECTIONS = generateDefaultActions("SET_LENDER_REPORTING_DRAWER_SELECTIONS");
export const setDrawerSelections = payload => {
  const data = payload;
  if (data.currentSectionName) {
    data.currentSection = drawerSections.find(x => x.name === data.currentSectionName);
    if (data.isEditingLineItem) {
      data.currentSection = { ...data.currentSection, title: "Edit line item" };
    }
  }

  return makeWritePayload(SET_LENDER_REPORTING_DRAWER_SELECTIONS.value, data);
};

export const GET_FIELDS_FOR_DELETION = generateDefaultActions("GET_FIELDS_FOR_DELETION");
export const getFieldsForDeletion = payload => makePayload(GET_FIELDS_FOR_DELETION.value, payload);
function* getFieldsForDeletionSaga({ dealId }) {
  const url = `/fields/deal/${dealId}`;
  yield requestSaga(GET_FIELDS_FOR_DELETION, url, "GET", null, null, null, true);
}

export const DELETE_SOURCE_FILES_BY_DEAL = generateDefaultActions("DELETE_SOURCE_FILES_BY_DEAL");
export const deleteSourceFilesByDeal = payload => makePayload(DELETE_SOURCE_FILES_BY_DEAL.value, payload);
function* deleteSourceFilesByDealSaga({ dealId }) {
  const url = `/source-files/all-files-for-deal/${dealId}`;
  yield requestSaga(DELETE_SOURCE_FILES_BY_DEAL, url, "DELETE", null, null, null, true);
}

export const SET_LENDER_REPORTING_DRAWER_TO_DEFAULT = generateDefaultActions("SET_LENDER_REPORTING_DRAWER_TO_DEFAULT");
export const setDrawerToDefault = () => makeWritePayload(SET_LENDER_REPORTING_DRAWER_TO_DEFAULT.value, initialState);


export const actionCreators = {
  getSourceFilesByDeal,
  setDrawerSelections,
  getAllFilesForDeal,
  getFieldsForDeletion,
  deleteSourceFilesByDeal,
  setDrawerToDefault
};

// REDUCER
export function lenderReportingDrawerReducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case GET_SOURCE_FILES_BY_DEAL.failure:
      ToastError("Failed to fetch existing source files.");

      return defaultReturn({ ...state, isLoading: false }, null, "dealSourceFiles");
    case GET_SOURCE_FILES_BY_DEAL.success:
      if (payload.data && !payload.data.length) {
        ToastInfo("There are no existing source files.");
      }

      return defaultReturn({ ...state, isLoading: false }, payload.data, "dealSourceFiles");
    case GET_SOURCE_FILES_BY_DEAL.value:
      return defaultReturn({ ...state, isLoading: true });

    case GET_ALL_FILES_FOR_DEAL.failure:
      ToastError("Failed to fetch existing source file versions.");

      return defaultReturn({ ...state, isLoading: false }, null, "allFilesForDeal");

    case GET_ALL_FILES_FOR_DEAL.success:
      return defaultReturn({ ...state, isLoading: false }, payload.data, "allFilesForDeal");

    case GET_ALL_FILES_FOR_DEAL.value:
      return defaultReturn({ ...state, isLoading: true });

    case SET_LENDER_REPORTING_DRAWER_SELECTIONS.value:
      return defaultReturn({ ...state, ...payload });

    case SET_LENDER_REPORTING_DRAWER_TO_DEFAULT.value:
      return defaultReturn({ ...state, ...payload });

    case GET_FIELDS_FOR_DELETION.success:
      return defaultReturn({ ...state }, payload.data, "fieldsForDeletion");

    case DELETE_SOURCE_FILES_BY_DEAL.success: {
      ToastSuccess("Source files deleted successfully");

      return defaultReturn({ ...state, dealSourceFiles: null });
    }
    case DELETE_SOURCE_FILES_BY_DEAL.failure:
      ToastError("Failed to delete source files");

      return defaultReturn({ ...state });
    case DELETE_SOURCE_FILES_BY_DEAL.value:
      return defaultReturn({ ...state });

    default:
      return state;
  }
}

// SAGA
export function* lenderReportingDrawerSaga() {
  yield takeLatest(GET_SOURCE_FILES_BY_DEAL.value, getSourceFilesByDealSaga);
  yield takeLatest(GET_ALL_FILES_FOR_DEAL.value, getAllFilesForDealSaga);
  yield takeLatest(GET_FIELDS_FOR_DELETION.value, getFieldsForDeletionSaga);
  yield takeLatest(DELETE_SOURCE_FILES_BY_DEAL.value, deleteSourceFilesByDealSaga);
}
